import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import { amountColumnHeader, useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { LoadMoreButton } from "./LoadMoreButton";
import { TableMessageRow } from "./TableMessageRow";

type Status = "owned" | "fulfillment_requested" | "withdrawn" | "sold" | "refunded";

const FILTERS: readonly { value: Status | undefined; label: string }[] = [
  { value: undefined, label: "All" },
  { value: "owned", label: "Owned" },
  { value: "fulfillment_requested", label: "In fulfillment" },
  { value: "withdrawn", label: "Shipped" },
  { value: "sold", label: "Sold back" },
  { value: "refunded", label: "Refunded" },
];

/** Every status names itself, so a new one fails typecheck here rather than reading as another. */
const STATUS_LABEL: Record<Status, string> = {
  owned: "owned",
  fulfillment_requested: "in fulfillment",
  withdrawn: "shipped",
  sold: "sold back",
  refunded: "refunded",
};

const STATUS_BADGE: Record<Status, "default" | "outline" | "secondary"> = {
  owned: "default",
  fulfillment_requested: "secondary",
  withdrawn: "outline",
  sold: "outline",
  refunded: "outline",
};

/** The items the user owns after claims (and the ones sold back), newest first, from `users.inventory`. */
export function UserInventoryTab({ endUserId }: { endUserId: string }) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const [status, setStatus] = useState<Status | undefined>(undefined);
  const query = useInfiniteQuery(
    trpc.users.inventory.infiniteQueryOptions(
      { endUserId, limit: 25, status },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <>
      <fieldset className="flex flex-wrap gap-1">
        <legend className="sr-only">Status</legend>
        {FILTERS.map((filter) => (
          <Button
            key={filter.label}
            type="button"
            size="sm"
            variant={status === filter.value ? "secondary" : "ghost"}
            aria-pressed={status === filter.value}
            onClick={() => setStatus(filter.value)}
          >
            {filter.label}
          </Button>
        ))}
      </fieldset>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Claimed</TableHead>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">{amountColumnHeader("Value", currency)}</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>From</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isLoading && <TableMessageRow colSpan={5}>Loading…</TableMessageRow>}
          {query.error && (
            <TableMessageRow colSpan={5} tone="error">
              {query.error.message}
            </TableMessageRow>
          )}
          {query.isSuccess && rows.length === 0 && (
            <TableMessageRow colSpan={5}>
              {status ? "No items with this status." : "No items yet; a claim puts one here."}
            </TableMessageRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="text-muted-foreground">{formatDate(row.createdAt)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {row.item.imageUrl && (
                    <img
                      src={row.item.imageUrl}
                      alt=""
                      className="size-8 rounded object-cover"
                      loading="lazy"
                    />
                  )}
                  <span>{row.item.name}</span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {formatAmount(row.item.value, currency)}
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_BADGE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.refundedInventoryItemId
                  ? "refund"
                  : row.battlePlayerId
                    ? "battle prize"
                    : row.battleOpenId
                      ? "battle"
                      : "open"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LoadMoreButton
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </>
  );
}
